import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { PHONE, PHONE_HREF, EMAIL, HOURS } from "@/lib/data";
import { Phone, Mail, MapPin, Clock, FileText } from "lucide-react";

const REASONS = ["Forklift Service", "Breakdown", "Maintenance", "Forklift Hire", "Forklift Purchase", "Parts", "General Enquiry"];

export default function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", company: "", phone: "", email: "", message: "",
    reason: params.get("reason") || "General Enquiry",
  });
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      await api.post("/enquiries", { kind: "contact", ...form, extra: { reason: form.reason } });
      toast.success("Thanks — we've received your enquiry.");
      setForm({ name: "", company: "", phone: "", email: "", message: "", reason: "General Enquiry" });
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div>
      <section className="bg-[#111111] text-white">
        <div className="max-w-7xl mx-auto px-6 py-16 ff-diagonal-bg">
          <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold">Get in touch</div>
          <h1 className="font-display text-5xl md:text-7xl uppercase mt-3">Contact Forkfix</h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-14">
        <div>
          <h2 className="ff-accent-heading font-display text-3xl uppercase mb-6">Direct contact</h2>
          <div className="space-y-4">
            <a href={PHONE_HREF} className="ff-card p-5 flex items-center gap-4 hover:border-[#D71920]" data-testid="contact-phone">
              <Phone className="w-6 h-6 text-[#D71920]" /><div><div className="text-xs uppercase tracking-widest text-[#666]">Phone</div><div className="font-display text-2xl">{PHONE}</div></div>
            </a>
            <a href={`mailto:${EMAIL}`} className="ff-card p-5 flex items-center gap-4 hover:border-[#D71920]" data-testid="contact-email">
              <Mail className="w-6 h-6 text-[#D71920]" /><div><div className="text-xs uppercase tracking-widest text-[#666]">Email</div><div className="font-display text-xl">{EMAIL}</div></div>
            </a>
            <div className="ff-card p-5 flex items-center gap-4">
              <MapPin className="w-6 h-6 text-[#D71920]" /><div><div className="text-xs uppercase tracking-widest text-[#666]">Service Area</div><div className="font-display text-xl">Brisbane, QLD & Surrounding Areas</div></div>
            </div>
            <div className="ff-card p-5 flex items-center gap-4">
              <Clock className="w-6 h-6 text-[#D71920]" /><div><div className="text-xs uppercase tracking-widest text-[#666]">Hours</div><div className="font-display text-xl">{HOURS}</div></div>
            </div>
            <div className="ff-card p-5 flex items-center gap-4">
              <FileText className="w-6 h-6 text-[#D71920]" /><div><div className="text-xs uppercase tracking-widest text-[#666]">ABN</div><div className="font-display text-xl font-mono-tech" data-testid="contact-abn">61 624 878 276</div></div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="ff-card p-8" data-testid="contact-form">
          <h2 className="font-display text-3xl uppercase mb-6">Send an enquiry</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="ff-label">Name*</label><input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="ff-input" data-testid="c-name" /></div>
            <div><label className="ff-label">Company</label><input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className="ff-input" /></div>
            <div><label className="ff-label">Phone*</label><input required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="ff-input" data-testid="c-phone" /></div>
            <div><label className="ff-label">Email*</label><input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="ff-input" data-testid="c-email" /></div>
            <div className="md:col-span-2"><label className="ff-label">Reason*</label>
              <select value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} className="ff-input">{REASONS.map(r => <option key={r}>{r}</option>)}</select></div>
            <div className="md:col-span-2"><label className="ff-label">Message*</label><textarea rows={5} required value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="ff-input" data-testid="c-message" /></div>
          </div>
          <button disabled={sending} className="ff-btn-primary mt-6 disabled:opacity-60" data-testid="c-submit">{sending ? "Sending…" : "Send Enquiry"}</button>
        </form>
      </section>
    </div>
  );
}
